'use client';
import { useRef, useState, useEffect } from 'react';
import Image from 'next/image';
import clsx from 'clsx';
import { resolveImageAltText } from '@/lib/utils';

/**
 * A block that displays a series of scrollable text sections with a sticky
 * image column on desktop. As the user scrolls through the text, the image
 * updates to match the active text section. On mobile, content is rendered
 * inline as text followed by image for each slide.
 */
export default function DetailFeature({ block, position }) {
  const {
    uid,
    slides = [],
    image_column_position = 'right'
  } = block?.detail_feature?.reference?.[0] ?? {};
  const [activeIndex, setActiveIndex] = useState(0);

  // CSS variables for the desktop sticky layout. Image height is clamped to the
  // viewport including padding to keep it visible even on weird aspect ratios.
  const desktopVars = {
    '--df-v-pad': '5rem',
    '--df-text-w': '25rem',

    // The section width + safe 7.5 gap from sides and middle
    '--df-section-w': 'calc(100vw - 7.5rem)',

    // The image is 4:3 according to the design, which we use below.
    // [ROY] If it weren't for supporting weird aspect ratios, we wouldn't need
    // any of these variables.
    '--df-image-h':
      'min(calc(100dvh - (var(--df-v-pad) * 2)), calc((var(--df-section-w) - var(--df-text-w)) * 3 / 4))',
    '--df-image-w': 'calc(var(--df-image-h) * 4 / 3)'
  };

  // Hold refs to each text slide element for the intersection observer
  const textRefs = useRef([]);

  // Track which image is active based on which text slide is visible
  useEffect(() => {
    const elementIndexMap = new Map();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const idx = elementIndexMap.get(entry.target);
          if (idx != null) setActiveIndex(idx);
        });
      },
      { rootMargin: '-49% 0% -49% 0%', threshold: 0 }
    );

    textRefs.current.forEach((el, i) => {
      if (!el) return;
      elementIndexMap.set(el, i);
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [slides.length]);

  if (!slides.length) return null;

  return (
    <section
      id={`${uid || 'detail-feature'}_${position}`}
      data-name="detail-feature"
      className="my-5 px-5 md:px-15 lg:px-5"
    >
      <div
        style={desktopVars}
        className={clsx(
          'relative flex gap-0 lg:items-start lg:gap-5',
          image_column_position === 'left'
            ? 'lg:flex-row-reverse'
            : 'lg:flex-row'
        )}
      >
        <DesktopTextColumn
          slides={slides}
          textRefs={textRefs}
          imageColumnPosition={image_column_position}
        />
        <SlideMediaColumn slides={slides} activeIndex={activeIndex} />
      </div>
    </section>
  );
}

/**
 * Renders the desktop-only text rail column and gradient overlay.
 */
function DesktopTextColumn({ slides, textRefs, imageColumnPosition }) {
  return (
    <div className="relative hidden lg:block lg:flex-1">
      <GradientOverlay />

      {slides.map((slide, i) => (
        <TextSlide
          key={i}
          slide={slide}
          imageColumnPosition={imageColumnPosition}
          textRef={(el) => {
            textRefs.current[i] = el;
          }}
        />
      ))}
    </div>
  );
}

/**
 * Renders one text slide in the scrolling rail.
 */
function TextSlide({ slide, textRef }) {
  const { heading, body } = slide ?? {};

  return (
    <div
      ref={textRef}
      className="flex flex-col justify-center px-5 py-20 lg:h-[calc(var(--df-image-h)+(var(--df-v-pad)*2))]"
    >
      <div className="mx-auto flex w-full max-w-100 flex-col gap-3">
        {heading && <h2 className="h3 lg:h2">{heading}</h2>}

        {body && (
          <div
            className="rte body-r"
            dangerouslySetInnerHTML={{ __html: body }}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Renders the slide media as a sticky transition stack on desktop and
 * a simple inline text followed by image layout on mobile.
 */
function SlideMediaColumn({ slides, activeIndex }) {
  return (
    <div
      className={clsx(
        'w-full lg:sticky lg:top-0 lg:flex-none lg:self-start',
        'lg:portrait:top-[25%]', // For high-res portrait tablets
        'lg:w-[var(--df-image-w)]'
      )}
    >
      <div
        className={clsx(
          'relative w-full lg:overflow-hidden',
          'lg:my-[var(--df-v-pad)] lg:h-[var(--df-image-h)] lg:w-[var(--df-image-w)]'
        )}
      >
        {slides.map((slide, i) => {
          const { heading, body, image } = slide ?? {};
          if (!image?.url) return null;

          return (
            <div
              key={i}
              className={clsx(
                'relative py-20',
                'lg:absolute lg:inset-0 lg:py-0 lg:transition-transform lg:duration-600 lg:ease-in-out',
                i <= activeIndex ? 'lg:translate-y-0' : 'lg:translate-y-full'
              )}
            >
              {/* Mobile-only text block */}
              <div className="lg:hidden">
                <div className="flex w-full max-w-100 flex-col gap-3">
                  {heading && <h2 className="h3 sm:h2">{heading}</h2>}
                  {body && (
                    <div
                      className="rte body-r"
                      dangerouslySetInnerHTML={{ __html: body }}
                    />
                  )}
                </div>
              </div>

              <div className="relative mt-10 aspect-[4/3] w-full overflow-hidden sm:mt-15 lg:absolute lg:inset-0 lg:mt-0">
                <Image
                  src={image.url}
                  alt={resolveImageAltText(image)}
                  fill
                  className={clsx(
                    'object-cover',
                    'lg:transition-transform lg:duration-600 lg:ease-in-out',
                    i === activeIndex ? 'lg:scale-[1.02]' : 'lg:scale-100'
                  )}
                  sizes="(min-width: 768px) 60vw, 100vw"
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Adds a desktop-only top/bottom fade overlay for the text column viewport.
 */
const GradientOverlay = () => (
  <div className="pointer-events-none absolute inset-0 z-1 hidden lg:block">
    <div className="sticky top-0 h-screen">
      <div className="absolute inset-x-0 top-0 h-56 bg-gradient-to-b from-white to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-white to-transparent" />
    </div>
  </div>
);
